import * as tf from '@tensorflow/tfjs'

// Candidate windows (normalized) for sliding-window localization: the whole
// frame plus overlapping windows at two smaller scales. A trained whole-frame
// classifier has no bounding box on its own, so we classify each window and the
// best-scoring one becomes the object's box. All windows run in ONE batched
// MobileNet pass, so this stays cheap.
function buildWindows() {
    const wins = [{ x: 0, y: 0, w: 1, h: 1 }]
    const grid = (size, steps) => {
        const stride = steps > 1 ? (1 - size) / (steps - 1) : 0
        for (let r = 0; r < steps; r++) {
            for (let c = 0; c < steps; c++) {
                wins.push({
                    x: c * stride,
                    y: r * stride,
                    w: size,
                    h: size,
                })
            }
        }
    }
    grid(0.6, 2) // 2x2 half-ish windows
    grid(0.4, 3) // 3x3 smaller windows
    return wins
}

const WINDOWS = buildWindows()

// Run the trained object classifier across the windows and return, per class,
// the best-scoring window as a box. `head` is the trained dense head, `mobilenet`
// the shared feature extractor. Returns [{classId, className, conf, box}].
export async function localizeObjects({
    video,
    mobilenet,
    head,
    classNames,
    classIds,
    minConf = 0.6,
}) {
    if (!video || !mobilenet || !head) return []
    let probs
    try {
        const feats = tf.tidy(() => {
            const frame = tf.browser.fromPixels(video).toFloat().expandDims()
            const boxes = WINDOWS.map((wn) => [
                wn.y,
                wn.x,
                wn.y + wn.h,
                wn.x + wn.w,
            ])
            const boxInd = WINDOWS.map(() => 0)
            const crops = tf.image
                .cropAndResize(frame, boxes, boxInd, [224, 224])
                .div(255)
            return mobilenet.predict(crops).reshape([WINDOWS.length, -1])
        })
        const logits = tf.tidy(() => head.predict(feats))
        probs = await logits.array()
        feats.dispose()
        logits.dispose()
    } catch (e) {
        return []
    }

    // Best window per class.
    const bestByClass = {}
    probs.forEach((row, wi) => {
        let top = 0
        for (let i = 1; i < row.length; i++) if (row[i] > row[top]) top = i
        const conf = row[top]
        const prev = bestByClass[top]
        if (!prev || conf > prev.conf) {
            bestByClass[top] = { conf, window: WINDOWS[wi] }
        }
    })

    const out = []
    Object.keys(bestByClass).forEach((k) => {
        const idx = Number(k)
        const { conf, window: wn } = bestByClass[idx]
        if (conf < minConf) return
        out.push({
            classId: classIds[idx],
            className: classNames[idx],
            conf,
            box: { x: wn.x, y: wn.y, w: wn.w, h: wn.h },
        })
    })
    return out
}
